import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet";

import {
  GlobeAltIcon,
  SparklesIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentListIcon,
  MapIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';

const AboutPage = () => {
  return (
    <main className="bg-gradient-to-b from-white via-slate-50 to-slate-100">
      {/* SEO META TAGS */}
      <Helmet>
        <title>Om Nordic Route | Modernt logistikföretag i Norden</title>
        <meta
          name="description"
          content="Nordic Route är ett modernt logistikföretag som kombinerar teknik, transparens och pålitlighet för att skapa framtidens nordiska logistiklösningar."
        />
        <link rel="canonical" href="https://www.nordicroute.se/about" />

        {/* STRUCTURED DATA */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Nordic Route",
            "description": "Ett modernt logistikföretag som erbjuder godstransport, expressleveranser och specialtransporter i Norden.",
            "url": "https://www.nordicroute.se",
            "areaServed": "Sweden, Norway, Denmark, Finland",
            "logo": "https://www.nordicroute.se/logo.png"
          }
          `}
        </script>
      </Helmet>

      {/* HERO */}
      <section
        id="about-hero"
        aria-labelledby="about-title"
        className="relative isolate overflow-hidden px-6 pt-32 pb-20 text-center lg:px-8"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl"
        >
          <div className="relative left-1/2 aspect-[1108/632] w-[72rem] -translate-x-1/2 
          bg-[radial-gradient(circle_at_top,_rgba(79,70,229,0.22),_transparent_60%),radial-gradient(circle_at_bottom,_rgba(56,189,248,0.2),_transparent_55%)] opacity-80" />
        </div>

        <div className="mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-700/80">
            Om Nordic Route
          </p>

          <h1 id="about-title" className="mt-4 text-balance text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            Ett modernt logistikföretag för en ny nordisk verklighet.
          </h1>

          <p className="mt-5 text-sm leading-7 text-slate-600 sm:text-base">
            Ett nytt logistikföretag byggt på modern teknik, transparens och nordisk pålitlighet.
            Vårt fokus ligger på framtiden, och på att göra transporter enklare att arbeta med varje dag.
          </p>
        </div>
      </section>

      {/* VILKA VI ÄR */}
      <section aria-labelledby="who-we-are" className="px-6 py-16 lg:px-8">
        <div className="mx-auto flex max-w-4xl flex-col gap-8 lg:flex-row lg:items-start">
          <div className="flex-shrink-0">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 ring-1 ring-indigo-100">
              <GlobeAltIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
            </div>
          </div>

          <div className="space-y-4">
            <h2 id="who-we-are" className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Vilka vi är
            </h2>

            <p className="text-sm leading-7 text-slate-600 sm:text-base">
              Nordic Route är ett nystartat logistikföretag med målet att skapa en mer transparent,
              effektiv och modern transportupplevelse i Norden. Vi bygger våra lösningar från grunden
              med fokus på teknik, pålitlighet och enkel kommunikation.
            </p>

            <p className="text-sm leading-7 text-slate-600 sm:text-base">
              Istället för att luta oss mot historik vill vi visa värde i varje leverans. Vi
              fokuserar på tydliga flöden, rak dialog och verktyg som ger både kunder och mottagare
              kontroll över sina transporter.
            </p>
          </div>
        </div>
      </section>

      {/* VISION & MÅL */}
      <section aria-labelledby="vision-title" className="px-6 py-16 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-2xl">
            <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-indigo-700/80">
              Vår vision & vårt mål
            </h2>

            <p id="vision-title" className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Framtidens nordiska logistikpartner.
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {/* Card 1 */}
            <article className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
                <SparklesIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                En nordisk logistikpartner som är enkel att arbeta med.
              </h3>
              <p className="text-sm leading-6 text-slate-600">
                Vår ambition är att göra logistiksamarbeten enklare – med tydliga kontaktvägar,
                genomtänkta flöden och en vardag där transportfrågor inte tar onödig tid.
              </p>
            </article>

            {/* Card 2 */}
            <article className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
                <GlobeAltIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                Bygga långsiktiga relationer med modern teknik.
              </h3>
              <p className="text-sm leading-6 text-slate-600">
                Vi kombinerar nordisk närvaro med digitala verktyg där spårning, uppföljning och
                kommunikation är integrerade från start.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* VARFÖR VÄLJA OSS */}
      <section aria-labelledby="why-choose-us" className="bg-gradient-to-b from-white via-white to-slate-50 px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <h2 id="why-choose-us" className="text-sm font-semibold uppercase tracking-[0.25em] text-indigo-700/80">
            Varför välja oss
          </h2>

          <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Ambitioner före historik.
          </p>

          <p className="mt-4 text-sm text-slate-600 sm:text-base">
            Som nystartat bolag kan vi bygga våra arbetssätt från grunden – med transparens,
            teknik och flexibilitet som utgångspunkt.
          </p>
        </div>

        {/* Cards */}
        <div className="mx-auto mt-12 grid max-w-5xl gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <article className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
              <ShieldCheckIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">Transparens från början</h3>
            <p className="text-sm leading-6 text-slate-600">
              Vi bygger våra processer med tydlig kommunikation i fokus – från bokning till leverans.
            </p>
          </article>

          <article className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
              <ArrowPathIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">Modern teknik</h3>
            <p className="text-sm leading-6 text-slate-600">
              Allt från spårning till kundkontakt ska vara modernt och intuitivt.
            </p>
          </article>

          <article className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
              <AdjustmentsHorizontalIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">Flexibilitet i flöden</h3>
            <p className="text-sm leading-6 text-slate-600">
              Som nystartat företag kan vi snabbt anpassa oss efter dina processer och behov.
            </p>
          </article>
        </div>
      </section>

      {/* VÅRT ARBETSSÄTT */}
      <section aria-labelledby="workflow" className="px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-2xl">
            <h2 id="workflow" className="text-sm font-semibold uppercase tracking-[0.25em] text-indigo-700/80">
              Vårt arbetssätt
            </h2>

            <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              En tydlig modell i tre steg.
            </p>

            <p className="mt-4 text-sm text-slate-600 sm:text-base">
              Vi vill att det ska vara enkelt att förstå hur vi arbetar – från första kontakt till leverans.
            </p>
          </div>

          {/* Steps */}
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            <article className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700">
                  1
                </span>
                <ClipboardDocumentListIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              </div>
              <h3 className="mt-2 text-base font-semibold text-slate-900">Planering</h3>
              <p className="text-sm leading-6 text-slate-600">
                Vi går igenom flöden, volymer och krav och sätter en tydlig plan.
              </p>
            </article>

            <article className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700">
                  2
                </span>
                <MapIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              </div>
              <h3 className="mt-2 text-base font-semibold text-slate-900">Ruttoptimering</h3>
              <p className="text-sm leading-6 text-slate-600">
                Vi optimerar rutter för bästa balans mellan ledtid, kostnad och hållbarhet.
              </p>
            </article>

            <article className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700">
                  3
                </span>
                <TruckIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              </div>
              <h3 className="mt-2 text-base font-semibold text-slate-900">Leverans med spårning</h3>
              <p className="text-sm leading-6 text-slate-600">
                Under hela resan erbjuder vi tydlig spårning och uppföljning.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        aria-labelledby="cta-about"
        className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 px-6 py-16 text-center text-slate-50 lg:px-8"
      >
        <div className="mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-300">
            Nästa steg
          </p>

          <h2
            id="cta-about"
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
          >
            Redo att starta ett samarbete?
          </h2>

          <p className="mt-4 text-sm text-slate-300 sm:text-base">
            Vi är ett ungt företag med stor drivkraft, korta beslutsvägar och fokus på långsiktiga samarbeten.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-full bg-indigo-500 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 transition hover:bg-indigo-400 hover:shadow-indigo-400/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              Kontakta oss
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
